import { PublicClientApplication } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { msalConfig, loginRequest, graphConfig, oneDriveConfig, validateConfig } from '../config/oneDriveConfig';

class OneDriveService {
  constructor() {
    this.msalInstance = null;
    this.graphClient = null;
    this.isAuthenticated = false;
  }

  /**
   * Inicializar el servicio de OneDrive
   */
  async initialize() {
    try {
      // Validar configuración antes de inicializar
      if (!validateConfig()) {
        throw new Error('Configuración de OneDrive incompleta. Revisa las variables de entorno.');
      }
      
      this.msalInstance = new PublicClientApplication(msalConfig);
      await this.msalInstance.initialize();
      
      // Verificar si ya hay una sesión activa
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
        await this.setupGraphClient();
        this.isAuthenticated = true;
      }
    } catch (error) {
      console.error('Error inicializando OneDrive service:', error);
      throw error;
    }
  }

  /**
   * Autenticar con Microsoft Graph
   */
  async authenticate() {
    try {
      console.log('Iniciando proceso de autenticación con Microsoft...');
      
      // Verificar si ya hay una cuenta activa
      const accounts = this.msalInstance.getAllAccounts();
      let account = null;
      
      if (accounts.length > 0) {
        account = accounts[0];
        this.msalInstance.setActiveAccount(account);
        console.log('Cuenta existente encontrada:', account.username);
      } else {
        console.log('No hay cuentas existentes, iniciando login popup...');
        const response = await this.msalInstance.loginPopup(loginRequest);
        account = response.account;
        this.msalInstance.setActiveAccount(account);
        console.log('Login exitoso para:', account.username);
      }
      
      // Obtener token de acceso
      console.log('Obteniendo token de acceso...');
      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account
      });
      
      console.log('Token obtenido exitosamente');
      
      // Configurar cliente de Graph
      this.graphClient = Client.init({
        authProvider: (done) => {
          done(null, tokenResponse.accessToken);
        }
      });
      
      // Verificar que el cliente funciona
      try {
        const userInfo = await this.graphClient.api('/me').get();
        console.log('Cliente de Graph configurado correctamente para:', userInfo.displayName);
      } catch (testError) {
        console.error('Error probando cliente de Graph:', testError);
        throw new Error('No se pudo configurar el cliente de Microsoft Graph');
      }
      
      this.isAuthenticated = true;
      console.log('Autenticación completada exitosamente');
      return true;
    } catch (error) {
      console.error('Error en autenticación:', error);
      console.error('Detalles del error de autenticación:', {
        message: error.message,
        errorCode: error.errorCode,
        errorMessage: error.errorMessage,
        correlationId: error.correlationId
      });
      this.isAuthenticated = false;
      throw error;
    }
  }

  /**
   * Configurar cliente de Microsoft Graph
   */
  async setupGraphClient() {
    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      throw new Error('No hay cuenta activa');
    }

    this.graphClient = Client.init({
      authProvider: async (done) => {
        try {
          const response = await this.msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: account
          });
          done(null, response.accessToken);
        } catch (error) {
          console.error('Error obteniendo token:', error);
          done(error, null);
        }
      }
    });
  }

  /**
   * Inicializar cliente de Microsoft Graph con AuthProvider
   */
  async initializeGraphClient() {
    try {
      const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
        this.msalInstance,
        {
          account: this.msalInstance.getActiveAccount(),
          scopes: loginRequest.scopes,
          interactionType: 'popup'
        }
      );
      
      this.graphClient = Client.initWithMiddleware({ authProvider });
    } catch (error) {
      console.error('Error inicializando Graph Client:', error);
      throw error;
    }
  }

  /**
   * Verificar si el acta está completa (aprobada y firmada)
   */
  isActaComplete(acta) {
    // Si es un acta externa, siempre está completa
    if (acta.isExternal) {
      return {
        complete: true,
        reason: 'Acta externa lista para subir'
      };
    }

    // Verificar que el acta esté aprobada
    if (!acta.approvalStatus || acta.approvalStatus !== 'aprobado') {
      return {
        complete: false,
        reason: 'El acta no ha sido aprobada'
      };
    }

    // Verificar que todos los asistentes hayan firmado
    const asistentesConFirma = acta.asistentes?.filter(asistente => asistente.firma) || [];
    const totalAsistentes = acta.asistentes?.length || 0;

    if (asistentesConFirma.length !== totalAsistentes || totalAsistentes === 0) {
      return {
        complete: false,
        reason: `Faltan firmas: ${asistentesConFirma.length}/${totalAsistentes} firmadas`
      };
    }

    return {
      complete: true,
      reason: 'Acta completa y lista para subir'
    };
  }

  /**
   * Subir acta a OneDrive
   */
  async uploadActa(actaData, pdfBlob) {
    try {
      console.log('🔄 Iniciando subida de acta a OneDrive...');
      console.log('📋 DATOS COMPLETOS DEL ACTA RECIBIDA:', JSON.stringify(actaData, null, 2));
      console.log('🏷️ TIPO DE ACTA ESPECÍFICO:', actaData.tipoActa);
      console.log('🏷️ TIPO DE ACTA CUSTOM:', actaData.tipoActaCustom);
      
      // Verificar que el acta esté completa
      const completionStatus = this.isActaComplete(actaData);
      if (!completionStatus.complete) {
        throw new Error(`No se puede subir el acta: ${completionStatus.reason}`);
      }

      // Verificar autenticación
      if (!this.isAuthenticated || !this.graphClient) {
        console.log('Autenticando con OneDrive...');
        await this.authenticate();
      }

      // Verificar permisos
      try {
        await this.graphClient.api('/me/drive').get();
        console.log('Permisos de OneDrive verificados correctamente');
      } catch (permError) {
        console.error('Error de permisos en OneDrive:', permError);
        throw new Error(`Sin permisos para acceder a OneDrive: ${permError.message}`);
      }

      // Crear carpeta principal si no existe
      console.log('Verificando/creando carpeta principal...');
      await this.createActasFolder();

      // Generar nombre del archivo usando la configuración
      const fileName = oneDriveConfig.fileNameFormat ? oneDriveConfig.fileNameFormat(actaData) : this.generateFileName(actaData);
      const folderPath = this.getFolderPath(actaData);
      console.log('Archivo:', fileName, 'Ruta:', folderPath);

      // Crear carpeta si no existe
      console.log('Creando estructura de carpetas...');
      await this.ensureFolderExists(folderPath);

      // Subir archivo
      const uploadPath = `${folderPath}/${fileName}`;
      console.log('Subiendo archivo a:', uploadPath);
      
      const uploadResponse = await this.graphClient
        .api(`/me/drive/root:${uploadPath}:/content`)
        .put(pdfBlob);

      console.log('Acta subida exitosamente a OneDrive:', uploadResponse);
      
      return {
        success: true,
        fileId: uploadResponse.id,
        fileName: fileName,
        webUrl: uploadResponse.webUrl,
        downloadUrl: uploadResponse['@microsoft.graph.downloadUrl']
      };
    } catch (error) {
      console.error('Error subiendo acta a OneDrive:', error);
      console.error('Detalles del error:', {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        requestId: error.requestId
      });
      throw error;
    }
  }

  /**
   * Generar nombre del archivo
   */
  generateFileName(actaData) {
    const fecha = actaData.fecha instanceof Date 
      ? actaData.fecha 
      : new Date(actaData.fecha);
    
    const fechaStr = fecha.toISOString().split('T')[0]; // YYYY-MM-DD
    const numeroActa = actaData.numeroActa || 'SN';
    
    // Si es un acta externa, agregar sufijo EXTERNA
    if (actaData.isExternal) {
      return `Acta_${numeroActa}_${fechaStr}_EXTERNA.pdf`;
    }
    
    return `Acta_${numeroActa}_${fechaStr}_FIRMADA.pdf`;
  }

  /**
   * Obtener ruta de carpeta para el acta basada en el tipo y contrato
   */
  getFolderPath(actaData) {
    const año = actaData.fecha instanceof Date 
      ? actaData.fecha.getFullYear()
      : new Date(actaData.fecha).getFullYear();
    
    // Determinar el tipo de acta
    let tipoActa = actaData.tipoActa;
    if (tipoActa === 'OTRO') {
      tipoActa = actaData.tipoActaCustom || 'OTRO';
    }
    
    // Debug logging para identificar problemas de clasificación
    console.log('🔍 Debug getFolderPath:', {
      tipoActaOriginal: actaData.tipoActa,
      tipoActaFinal: tipoActa,
      tipoActaCustom: actaData.tipoActaCustom,
      actaData: actaData
    });
    
    // Normalizar tipoActa para comparación (convertir a mayúsculas y limpiar)
    const tipoActaNormalizado = tipoActa ? tipoActa.toUpperCase().trim() : '';
    
    // Categorización por carpetas según el tipo
    if (tipoActaNormalizado === 'REUNION' || tipoActaNormalizado.includes('REUNIÓN') || tipoActaNormalizado.includes('SEGUIMIENTO')) {
      // Para reuniones de seguimiento, crear carpeta por contrato
      const contrato = this.getContratoFromActa(actaData);
      const ruta = `/Actas/${año}/Reunion_Seguimiento/${contrato}`;
      console.log('📁 Clasificado como REUNION:', ruta);
      return ruta;
    } else if (tipoActaNormalizado && (tipoActaNormalizado.startsWith('COMITE') || tipoActaNormalizado.includes('COMITÉ'))) {
      // Para comités, todos en la misma carpeta
      const ruta = `/Actas/${año}/Comite`;
      console.log('📁 Clasificado como COMITE:', ruta);
      return ruta;
    } else {
      // Para otros tipos, todos en la misma carpeta
      const ruta = `/Actas/${año}/Otros`;
      console.log('📁 Clasificado como OTROS:', ruta);
      return ruta;
    }
  }

  /**
   * Extraer información del contrato/proyecto del acta
   */
  getContratoFromActa(actaData) {
    console.log('🔍 Debug getContratoFromActa:', {
      tipoProyecto: actaData.tipoProyecto,
      tipoProyectos: actaData.tipoProyectos,
      proyecto: actaData.proyecto
    });
    
    // Priorizar tipoProyecto si existe
    if (actaData.tipoProyecto && actaData.tipoProyecto.trim()) {
      const contrato = this.sanitizeFolderName(actaData.tipoProyecto);
      console.log('📋 Contrato extraído de tipoProyecto:', contrato);
      return contrato;
    }
    
    // Si hay múltiples proyectos, usar el primero
    if (actaData.tipoProyectos && actaData.tipoProyectos.length > 0) {
      const contrato = this.sanitizeFolderName(actaData.tipoProyectos[0]);
      console.log('📋 Contrato extraído de tipoProyectos[0]:', contrato);
      return contrato;
    }
    
    // Si hay información de proyecto en el título
    if (actaData.proyecto && actaData.proyecto.trim()) {
      const contrato = this.sanitizeFolderName(actaData.proyecto);
      console.log('📋 Contrato extraído de proyecto:', contrato);
      return contrato;
    }
    
    // Fallback a 'General' si no se encuentra información específica
    console.log('📋 Contrato fallback: General');
    return 'General';
  }

  /**
   * Limpiar nombre de carpeta para que sea válido en OneDrive
   */
  sanitizeFolderName(name) {
    if (!name || typeof name !== 'string') {
      return 'General';
    }
    
    // Remover caracteres no válidos para nombres de carpeta en OneDrive
    // OneDrive no permite: \ / : * ? " < > |
    return name
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 100) || 'General'; // Limitar longitud
  }

  /**
   * Crear carpetas principales de actas
   */
  async createActasFolder() {
    try {
      console.log('Verificando carpetas principales de actas...');
      
      const foldersToCreate = [
        { name: oneDriveConfig.actasFolder, description: 'carpeta principal de actas' },
        { name: 'Actas_iets', description: 'carpeta adicional Actas_iets' }
      ];
      
      const createdFolders = [];
      
      for (const folderConfig of foldersToCreate) {
        try {
          // Verificar si la carpeta ya existe
          const checkUrl = `/me/drive/root:/${folderConfig.name}`;
          console.log(`Verificando existencia de ${folderConfig.description} con URL:`, checkUrl);
          
          const folder = await this.graphClient.api(checkUrl).get();
          console.log(`${folderConfig.description} ya existe:`, folder.id, folder.name);
          createdFolders.push(folder);
        } catch (error) {
          console.log(`Error verificando ${folderConfig.description}:`, error.code, error.message);
          
          if (error.code === 'itemNotFound' || error.statusCode === 404) {
            console.log(`Creando ${folderConfig.description}...`);
            
            try {
              // Crear la carpeta
              const createUrl = '/me/drive/root/children';
              console.log(`Creando ${folderConfig.description} con URL:`, createUrl);
              console.log('Nombre de carpeta:', folderConfig.name);
              
              const newFolder = await this.graphClient.api(createUrl).post({
                name: folderConfig.name,
                folder: {},
                '@microsoft.graph.conflictBehavior': 'rename'
              });
              
              console.log(`${folderConfig.description} creada exitosamente:`, newFolder.id, newFolder.name);
              createdFolders.push(newFolder);
            } catch (createError) {
              console.error(`Error creando ${folderConfig.description}:`, {
                message: createError.message,
                code: createError.code,
                statusCode: createError.statusCode,
                requestUrl: createUrl,
                folderName: folderConfig.name
              });
              throw createError;
            }
          } else {
            console.error(`Error inesperado verificando ${folderConfig.description}:`, error);
            throw error;
          }
        }
      }
      
      // Retornar la carpeta principal 'Actas' (la primera)
      return createdFolders.find(folder => folder.name === oneDriveConfig.actasFolder) || createdFolders[0];
    } catch (error) {
      console.error('Error en createActasFolder:', error);
      console.error('Detalles completos del error:', {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        requestId: error.requestId
      });
      throw error;
    }
  }

  /**
   * Asegurar que existe una carpeta en la ruta especificada
   */
  async ensureFolderExists(folderPath) {
    try {
      console.log('Verificando existencia de carpeta:', folderPath);
      
      // Dividir la ruta en partes
      const pathParts = folderPath.split('/').filter(part => part.length > 0);
      let currentPath = '';
      
      for (const part of pathParts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        
        try {
          // Verificar si la carpeta existe
          console.log(`Verificando carpeta: ${currentPath}`);
          const checkUrl = `/me/drive/root:/${currentPath}`;
          console.log(`URL de verificación: ${checkUrl}`);
          
          await this.graphClient.api(checkUrl).get();
          console.log(`Carpeta existe: ${currentPath}`);
        } catch (error) {
          console.log(`Error verificando carpeta ${currentPath}:`, error.code, error.message);
          
          if (error.code === 'itemNotFound' || error.statusCode === 404) {
            console.log(`Creando carpeta: ${currentPath}`);
            
            // Determinar la ruta del padre
            const parentParts = currentPath.split('/');
            parentParts.pop(); // Remover la última parte
            const parentPath = parentParts.join('/');
            
            // Construir la URL de la API para crear la carpeta
            let parentApiPath;
            if (parentPath) {
              parentApiPath = `/me/drive/root:/${parentPath}:/children`;
            } else {
              parentApiPath = '/me/drive/root/children';
            }
            
            console.log(`URL para crear carpeta: ${parentApiPath}`);
            console.log(`Nombre de carpeta a crear: ${part}`);
            
            try {
              const createResponse = await this.graphClient.api(parentApiPath).post({
                name: part,
                folder: {},
                '@microsoft.graph.conflictBehavior': 'rename'
              });
              
              console.log(`Carpeta creada exitosamente: ${currentPath}`, createResponse.id);
            } catch (createError) {
              console.error(`Error creando carpeta ${part} en ${parentPath}:`, {
                message: createError.message,
                code: createError.code,
                statusCode: createError.statusCode,
                requestUrl: parentApiPath
              });
              throw createError;
            }
          } else {
            console.error(`Error inesperado verificando carpeta ${currentPath}:`, error);
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error en ensureFolderExists:', error);
      console.error('Detalles completos del error:', {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        requestId: error.requestId,
        folderPath: folderPath
      });
      throw error;
    }
  }

  /**
   * Obtener lista de actas en OneDrive
   */
  async getActasList(year = null) {
    try {
      if (!this.isAuthenticated || !this.graphClient) {
        await this.authenticate();
      }

      const searchPath = year ? `/Actas/${year}` : '/Actas';
      
      const response = await this.graphClient
        .api(`/me/drive/root:${searchPath}:/children`)
        .get();

      return response.value.filter(item => item.file && item.name.endsWith('.pdf'));
    } catch (error) {
      console.error('Error obteniendo lista de actas:', error);
      throw error;
    }
  }

  /**
   * Descargar acta desde OneDrive
   */
  async downloadActa(fileId) {
    try {
      if (!this.isAuthenticated || !this.graphClient) {
        await this.authenticate();
      }

      const response = await this.graphClient
        .api(`/me/drive/items/${fileId}/content`)
        .get();

      return response;
    } catch (error) {
      console.error('Error descargando acta:', error);
      throw error;
    }
  }

  /**
   * Verificar si el usuario está autenticado
   */
  isUserAuthenticated() {
    return this.isAuthenticated && this.graphClient;
  }

  /**
   * Función de diagnóstico para verificar el estado de OneDrive
   */
  async runDiagnostics() {
    const diagnostics = {
      authentication: false,
      permissions: false,
      driveAccess: false,
      folderCreation: false,
      errors: []
    };

    try {
      // 1. Verificar autenticación
      console.log('🔍 Diagnóstico 1: Verificando autenticación...');
      if (!this.isAuthenticated || !this.graphClient) {
        await this.authenticate();
      }
      diagnostics.authentication = true;
      console.log('✅ Autenticación: OK');

      // 2. Verificar acceso básico a OneDrive
      console.log('🔍 Diagnóstico 2: Verificando acceso a OneDrive...');
      const driveInfo = await this.graphClient.api('/me/drive').get();
      diagnostics.driveAccess = true;
      console.log('✅ Acceso a OneDrive: OK', driveInfo.driveType);

      // 3. Verificar permisos específicos
      console.log('🔍 Diagnóstico 3: Verificando permisos...');
      try {
        await this.graphClient.api('/me/drive/root/children').get();
        diagnostics.permissions = true;
        console.log('✅ Permisos de lectura/escritura: OK');
      } catch (permError) {
        diagnostics.errors.push(`Permisos insuficientes: ${permError.message}`);
        console.log('❌ Permisos de lectura/escritura: FALLO');
      }

      // 4. Probar creación de carpeta de prueba
      console.log('🔍 Diagnóstico 4: Probando creación de carpetas...');
      try {
        const testFolderName = `test_${Date.now()}`;
        const testFolder = await this.graphClient.api('/me/drive/root/children').post({
          name: testFolderName,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'rename'
        });
        
        // Eliminar carpeta de prueba
        await this.graphClient.api(`/me/drive/items/${testFolder.id}`).delete();
        
        diagnostics.folderCreation = true;
        console.log('✅ Creación de carpetas: OK');
      } catch (folderError) {
        diagnostics.errors.push(`Error creando carpetas: ${folderError.message}`);
        console.log('❌ Creación de carpetas: FALLO');
      }

    } catch (error) {
      diagnostics.errors.push(`Error general: ${error.message}`);
      console.error('❌ Diagnóstico falló:', error);
    }

    console.log('📊 Resumen de diagnósticos:', diagnostics);
    return diagnostics;
  }

  /**
   * Cerrar sesión
   */
  async logout() {
    try {
      await this.msalInstance.logoutPopup();
      this.isAuthenticated = false;
      this.graphClient = null;
    } catch (error) {
      console.error('Error cerrando sesión OneDrive:', error);
      throw error;
    }
  }
}

// Instancia singleton
const oneDriveService = new OneDriveService();

export default oneDriveService;

// Funciones de utilidad
export const uploadActaToOneDrive = async (actaData, pdfBlob) => {
  return await oneDriveService.uploadActa(actaData, pdfBlob);
};

export const checkActaCompleteness = (actaData) => {
  return oneDriveService.isActaComplete(actaData);
};

export const initializeOneDrive = async () => {
  return await oneDriveService.initialize();
};